import React, { useEffect, useRef, useState } from "react";
import { api } from "../utils/Api";
import axios from "axios";
import {
	Button,
	Flex,
	Typography,
	Modal,
	Statistic,
	Tag,
	notification,
} from "antd";
import {
	CustomSearch,
	CustomSpinner,
	CustomTable,
} from "../utils/CustomComponents";
import dayjs from "dayjs";
import { DollarOutlined } from "@ant-design/icons";
import PaymentJob from "../job/PaymentJob";

const { Text } = Typography;

const OpenInvoices = ({ customerId }) => {
	const [loading, setLoading] = useState(true);
	const [invoices, setInvoices] = useState([]);
	const [startDate, setStartDate] = useState(dayjs());
	const [endDate, setEndDate] = useState(dayjs());
	const [modalInvoiceOpen, setModalInvoiceOpen] = useState(false);
	const jobId = useRef(null);
	const [apiNotification, contextHolder] = notification.useNotification();

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	/**
	 ** Options header (columns)
	 */
	const columns = [
		{
			title: "Date",
			dataIndex: "date",
			key: "date",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Name",
			dataIndex: "customer",
			key: "customer",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Invoice ID",
			dataIndex: "invoiceId",
			key: "invoiceId",
			render: (value) => {
				return <Text strong>{value}</Text>;
			},
		},
		{
			title: "Amount",
			dataIndex: "amount",
			key: "amount",
			render: (value) => {
				return (
					<Statistic
						value={value}
						precision={2}
						valueStyle={{
							fontSize: 14,
						}}
					/>
				);
			},
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (value, record) => {
				let color = "black";
				switch (record.statusType) {
					case "overdue":
						color = "red";
						break;
					case "due":
						color = "green";
						break;
					case "today":
						color = "blue";
						break;
					default:
						color = "black";
				}
				return <Tag color={color}>{value}</Tag>;
			},
		},
		{
			title: "Action",
			dataIndex: "action",
			width: 200,
			render: (_, record) => {
				return (
					<Flex justify="space-between">
						<Button
							type="primary"
							icon={<DollarOutlined twoToneColor={"white"} />}
							onClick={() => {
								jobId.current = record.jobId;
								setModalInvoiceOpen(true);
							}}
						/>
					</Flex>
				);
			},
		},
	];

	useEffect(() => {
		getInvoices();
	}, [startDate, endDate]);

	const getInvoices = async () => {
		try {
			setLoading(true);
			const start = `${startDate.year()}-${
				startDate.month() + 1
			}-${startDate.date()}`;
			const end = `${endDate.year()}-${endDate.month() + 1}-${endDate.date()}`;

			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };
			const url =
				api +
				`/invoice/get/bycustomer?startDate=${start}&endDate=${end}&client=${customerId}`;
			const { data } = await axios.get(url);
			if (data.success) {
				const dataList = [];
				data.payload.map((invoice, index) => {
					const invoiceDate = dayjs(invoice.invoiceDate);
					const currentDate = dayjs();
					let status = "";
					const paymentTerm = invoice.paymentTerm;
					const diff = currentDate.diff(invoiceDate, "day");
					let statusType = "today";
					if (paymentTerm === "7") {
						if (diff > 7) {
							status = `Past Due ${diff - 7} days`;
							statusType = "overdue";
						} else {
							if (7 - diff === 0) {
								status = "Due Today";
								statusType = "today";
							} else {
								status = `Due in ${7 - diff} days`;
								statusType = "due";
							}
						}
					} else if (paymentTerm === "14") {
						if (diff > 14) {
							status = `Past Due ${diff - 14} days`;
							statusType = "overdue";
						} else {
							if (14 - diff === 0) {
								status = "Due Today";
								statusType = "today";
							} else {
								status = `Due in ${14 - diff} days`;
								statusType = "due";
							}
						}
					} else if (paymentTerm === "21") {
						if (diff > 21) {
							status = `Past Due ${diff - 21} days`;
							statusType = "overdue";
						} else {
							if (21 - diff === 0) {
								status = "Due Today";
								statusType = "today";
							} else {
								status = `Due in ${21 - diff} days`;
								statusType = "due";
							}
						}
					} else if (paymentTerm === "30") {
						if (diff > 30) {
							status = `Past Due ${diff - 30} days`;
							statusType = "overdue";
						} else {
							if (30 - diff === 0) {
								status = "Due Today";
								statusType = "today";
							} else {
								status = `Due in ${30 - diff} days`;
								statusType = "due";
							}
						}
					} else if (paymentTerm === "Prepayment") {
						status = "Due Today";
						statusType = "today";
					}

					dataList.push({
						index: index,
						key: invoice._id,
						jobId: invoice.job,
						date: dayjs(invoice.invoiceDate).format("MM/DD/YYYY"),
						customer: invoice.customer.name,
						invoiceId: invoice.invoiceNumber,
						amount: invoice.invoiceTotal,
						status: status,
						statusType: statusType,
					});
				});
				setInvoices(dataList);
			} else {
				openNotification("error", "Error", data.message);
			}

			setLoading(false);
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", "Error while trying to get invoices");
			return null;
		}
	};

	const onRangeChange = (dates) => {
		if (dates) {
			setStartDate(dates[0]);
			setEndDate(dates[1]);
		}
	};

	const RenderSearch = () => {
		return (
			<CustomSearch
				loading={loading}
				startDate={startDate}
				endDate={endDate}
				onRangeChange={onRangeChange}
				dateFormat={"MM/DD/YYYY"}
			/>
		);
	};

	const RenderJobTable = () => {
		return (
			<Flex gap={"middle"} vertical>
				<CustomTable
					columns={columns}
					data={invoices}
					isScroll={false}
					loading={loading}
				/>
			</Flex>
		);
	};

	const paymentsUpdated = () => {
		getInvoices();
		setModalInvoiceOpen(false);
		jobId.current = null;
	};

	const RenderModalInvoice = () => {
		return (
			<Modal
				title="Invoice Payments"
				open={modalInvoiceOpen}
				onCancel={() => setModalInvoiceOpen(false)}
				cancelButtonProps={{ style: { display: "none" } }}
				okButtonProps={{ style: { display: "none" } }}
				width={1200}
			>
				<PaymentJob jobId={jobId.current} pcb={paymentsUpdated} />
			</Modal>
		);
	};

	return (
		<div>
			{loading ? (
				<CustomSpinner />
			) : (
				<Flex gap={"middle"} vertical>
					<RenderSearch />
					<RenderJobTable />
					<RenderModalInvoice />
				</Flex>
			)}
			{contextHolder}
		</div>
	);
};

export default OpenInvoices;
